import type { CartItem } from '~/composables/types/cart'
import type { EventDescription } from '~/composables/types/event'
import type { GtmItemListId } from '~/composables/useKftGtm/types'
import type {
  AlgoliaEventItem,
  CategoryItem,
} from '~/composables/types/algolia'
import { sendViewItemListConfig } from '~/composables/useKftGtm/sendViewItemListConfig'
import {
  persistItemListAttribution,
  enrichGtmItemsWithListAttribution,
} from '~/utils/gtmItemListAttribution'
import { eventGetters } from '~/utils/eventGetters'
import type { Wishlist } from '~/composables/useWishlist'
import { useCartWithSubdomainLogic } from '~/composables/useCartWithSubdomainLogic'
import { cartGetters } from '~/utils/cartGetters'

export const sendAddToCart = (cartItem: CartItem) => {
  const $gtm = useKftGtm()

  if (!$gtm) {
    return
  }

  // Calculate total value including addons
  const totalValue = cartGetters.getItemTotalPrice(cartItem)

  // GA4 object
  $gtm.pushToDataLayer({ ecommerce: null })

  const items = cartGetters.getItemsForGtm({
    items: [cartItem],
    updatedAt: null,
  })
  const obj = {
    event: 'add_to_cart',
    ecommerce: {
      currency: 'EUR',
      value: parseFloat(totalValue.toFixed(2)),
      items,
    },
  }

  $gtm.pushToDataLayer(obj)
}

export const sendProductDetailView = (event: EventDescription) => {
  const $gtm = useKftGtm()

  if (!$gtm) {
    return
  }

  const price = eventGetters.getDefaultPriceAmount(event)

  const gtmPrice = Number(price) / 100

  setTimeout(() => {
    const items = enrichGtmItemsWithListAttribution([
      {
        item_id: eventGetters.getId(event),
        item_brand: eventGetters.getSupplierName(event) || '',
        item_name: eventGetters.getTitle(event),
        item_category: eventGetters.getMainCategory(event),
        price: gtmPrice.toFixed(2),
        quantity: 1,
        google_business_vertical: 'custom',
        ...(eventGetters.isDateRequestActive(event) &&
        eventGetters.isInstantBookingActive(event)
          ? { item_category5: 'request-to-book' }
          : {}),
      },
    ])

    $gtm.pushToDataLayer({ ecommerce: null })
    $gtm.pushToDataLayer({
      event: 'view_item',
      ecommerce: {
        currency: 'EUR',
        value: parseFloat(gtmPrice.toFixed(2)),
        items,
      },
    })
  }, 2200)
}

export const sendViewItemList = (
  itemListId: GtmItemListId,
  items: (EventDescription | CategoryItem)[]
) => {
  const $gtm = useKftGtm()

  if (!$gtm || !itemListId) {
    return
  }

  const { listName, parser } = sendViewItemListConfig[itemListId]

  const itemListData = {
    item_list_id: itemListId,
    item_list_name: listName,
  }

  const parsedItems = items.map((item, index) => ({
    ...parser(item),
    ...itemListData,
    index,
  }))

  setTimeout(() => {
    $gtm.pushToDataLayer({ ecommerce: null })
    $gtm.pushToDataLayer({
      event: 'view_item_list',
      ecommerce: {
        ...itemListData,
        items: parsedItems,
        currency: 'EUR',
      },
    })
  }, 1200)
}

export const sendSelectListItem = (
  itemListId: GtmItemListId,
  items: (EventDescription | CategoryItem)[],
  i: number
) => {
  const $gtm = useKftGtm()
  if (!$gtm || !itemListId) {
    return
  }

  const { listName, parser } = sendViewItemListConfig[itemListId]

  const itemListData = {
    item_list_id: itemListId,
    item_list_name: listName,
  }

  const parsedItems = items.map((item) => {
    const parsedItem = parser(item)
    persistItemListAttribution(parsedItem.item_id, itemListData)
    return {
      ...parsedItem,
      ...itemListData,
      index: i,
    }
  })

  $gtm.pushToDataLayer({ ecommerce: null })
  $gtm.pushToDataLayer({
    event: 'select_item',
    ecommerce: {
      ...itemListData,
      items: parsedItems,
      currency: 'EUR',
    },
  })
}

export const sendViewCart = () => {
  const { cart } = useCartWithSubdomainLogic()

  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }
  $gtm.pushToDataLayer({ ecommerce: null })
  $gtm.pushToDataLayer({
    event: 'view_cart',
    ecommerce: {
      currency: 'EUR',
      value: cartGetters.getTotals(cart.value).total,
      items: cartGetters.getItemsForGtm(cart.value),
    },
  })
}
export const sendPrivateEventRequestForm = (
  requestLocation: string,
  cart_value: number,
  sha256_email?: string
) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'submit_perequest',
    request_location: requestLocation,
    cart_value,
    ...(sha256_email ? { sha256_email } : {}),
  })
}
export const sendWaitlistForm = (sha256_email?: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'submit_waitlist',
    ...(sha256_email ? { sha256_email } : {}),
  })
}

export const sendDateRequestForm = (
  requestLocation: string,
  cart_value: number,
  sha256_email?: string
) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'submit_daterequest',
    request_location: requestLocation,
    cart_value,
    ...(sha256_email ? { sha256_email } : {}),
  })
}
export const sendDateRequestSidebarView = (requestLocation: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'view_daterequest_sidebar',
    request_location: requestLocation,
  })
}

export const sendWaitlistSidebarView = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'view_waitlist_sidebar',
  })
}

export const sendGalleryShareIntent = (event: any, type: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }
  $gtm.pushToDataLayer({
    event: 'share_event_modal',
    event_id: eventGetters.getId(event),
    event_name: eventGetters.getTitle(event),
    sharing_type: type,
  })
}

export const sendGalleryShareOption = (event: any, type: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }
  $gtm.pushToDataLayer({
    event: 'share_event',
    event_id: eventGetters.getId(event),
    event_name: eventGetters.getTitle(event),
    sharing_options: type,
  })
}

export const sendGalleryShareNativeStatus = (event: any, status: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }
  $gtm.pushToDataLayer({
    event: 'share_event_status',
    event_id: eventGetters.getId(event),
    event_name: eventGetters.getTitle(event),
    sharing_type: 'native',
    sharing_status: status,
  })
}

export const sendPricingOptions = (option: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'select_pricing_plan',
    pricing_option: option,
  })
}

export const sendNewsletterSubscription = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'submit_newsletter',
  })
}

export const sendAddToWishlist = (item: AlgoliaEventItem) => {
  const $gtm = useKftGtm()

  if (!$gtm) {
    return
  }

  const gtmPrice = algoliaEventGetters.getPriceAmount(item) / 100

  // GA4 object
  $gtm.pushToDataLayer({ ecommerce: null })
  const obj = {
    event: 'add_to_wishlist',
    ecommerce: {
      currency: 'EUR',
      value: parseFloat(gtmPrice.toFixed(2)),
      items: [
        {
          item_name: algoliaEventGetters.fnGetTitle(item),
          item_id: algoliaEventGetters.fnGetHashedId(item),
          price: gtmPrice.toFixed(2),
          item_brand: algoliaEventGetters.fnGetSupplier(item) || 'konfetti',
          item_category: algoliaEventGetters.fnGetMainCategory(item),
          google_business_vertical: 'custom',
        },
      ],
    },
  }

  $gtm.pushToDataLayer(obj)
}

export const sendViewWishlist = (wishlist: Wishlist) => {
  if (!wishlist?.items) return
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }
  $gtm.pushToDataLayer({ ecommerce: null })
  $gtm.pushToDataLayer({
    event: 'view_wishlist',
    ecommerce: {
      currency: 'EUR',
      items: algoliaEventGetters.getItemsForGtm(
        wishlist.items.map((item) => item.object)
      ),
    },
  })
}

export const sendFilterOpen = (filter: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'open_filter',
    filter,
  })
}

export const sendFilterSelected = (filter: string, value: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'select_filter',
    filter,
    filter_option: value,
  })
}

// GA4 site-search event. `resultsCount` is 0 for zero-result searches, which is
// how we track no-hit queries. Result-click and search→booking attribution
// (Algolia Insights queryID parity) is intentionally out of scope here.
export const sendSearch = (searchTerm: string, resultsCount: number) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  const term = searchTerm?.trim()
  if (!term) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'search',
    search_term: term,
    search_results_count: resultsCount,
  })
}

export const sendOrderPurchase = (order: any, revenue?: number) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  // Parse items synchronously — order-success clears sessionStorage right after calling this
  const items = orderGetters.parseItemsForGtm(order)

  setTimeout(() => {
    $gtm.pushToDataLayer({ ecommerce: null })
    $gtm.pushToDataLayer({
      event: 'purchase',
      sha256_email: orderGetters.getShaEmailAddress(order),
      user_id: orderGetters.getUserId(order),
      country_code: orderGetters.getCountryCode(order),
      ecommerce: {
        currency: 'EUR',
        value: revenue?.toFixed(2),
        transaction_id: order.id,
        coupon: order.coupon?.data?.code ? order.coupon.data.code : '',
        marketing_campaign_id: order?.coupon?.data?.mkt_campaign_id || '',
        items,
      },
    })
  }, 1200)
}

export const sendCheckout = (cart: any, step: number) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  const eventNameByStep = {
    0: 'begin_checkout',
    1: 'add_payment_info',
  }

  const eventName = eventNameByStep[step as keyof typeof eventNameByStep]

  setTimeout(() => {
    $gtm.pushToDataLayer({ ecommerce: null })
    $gtm.pushToDataLayer({
      event: eventName,
      ecommerce: {
        currency: 'EUR',
        value: cartGetters.getTotals(cart).total,
        items: cartGetters.getItemsForGtm(cart),
      },
    })
  }, 1200)
}

export const sendAddShippingInfo = (cart: any) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  setTimeout(() => {
    $gtm.pushToDataLayer({ ecommerce: null })
    $gtm.pushToDataLayer({
      event: 'add_shipping_info',
      ecommerce: {
        currency: 'EUR',
        value: cartGetters.getTotals(cart).total,
        items: cartGetters.getItemsForGtm(cart),
      },
    })
  }, 1200)
}

export const sendViewLogin = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'view_login',
  })
}

export const sendViewGuestLogin = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'view_guest',
  })
}

export const sendGuestContact = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'submit_guest_contact',
    method: 'guest',
  })
}

export const sendViewSignup = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'view_signup',
  })
}

export const sendLogin = (method: string, user_id: string | null) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'login',
    method,
    user_id,
  })
}

export const sendSignup = (method: string, user_id: string) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'signup',
    method,
    user_id,
  })
}

export const sendLogout = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'logout',
    user_id: '',
  })
}

export const sendCorporateGiftRequestGTMEvent = (
  eventType: 'GIFT_CARD' | 'BOX'
) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'corporate_gift_request',
    event_type: eventType,
  })
}

export const sendPartnerRegistration = (step: 1 | 2 | 3) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: `partner_registration_step_${step}`,
  })
}

export const sendPartnerOnboardingSubmit = () => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'partner_application',
  })
}

export interface MenuClickParams {
  menuName: string
  sectionTitle?: string
  subSectionTitle?: string
  itemLabel: string
  itemPath: string
}

export const sendMenuClick = (params: MenuClickParams) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  const { menuName, sectionTitle, subSectionTitle, itemLabel, itemPath } =
    params

  const categoryParts = [sectionTitle, subSectionTitle].filter(Boolean)
  const menu_category = categoryParts.length ? categoryParts.join(' > ') : 'n/a'

  const labelParts = [
    menuName,
    sectionTitle,
    subSectionTitle,
    itemLabel,
  ].filter(Boolean)

  // Deduplicate: if itemLabel equals menuName and no sections, just use menuName
  const menu_item_label =
    labelParts.length === 1 ||
    (labelParts.length === 2 &&
      labelParts[0] === labelParts[1] &&
      !sectionTitle &&
      !subSectionTitle)
      ? menuName
      : labelParts.join(' > ')

  $gtm.pushToDataLayer({
    event: 'menu_click',
    menu_name: menuName,
    menu_category,
    menu_item_label,
    menu_item_path: itemPath,
  })
}

export type StickyBuyBoxComponentType =
  | 'giftcard'
  | 'promotion'
  | 'multidate'
  | 'tiered'
  | 'kit'
  | 'standard'

export const sendStickyBuyBoxClick = (
  componentType: StickyBuyBoxComponentType
) => {
  const $gtm = useKftGtm()
  if (!$gtm) {
    return
  }

  $gtm.pushToDataLayer({
    event: 'click_sticky_buybox',
    component_type: componentType,
  })
}
